import clsx from 'clsx'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import shallowEqualWithNestedProps from 'lib/memoize/shallowEqualWithNestedProps'
import { rem } from 'polished'
import type { ComponentProps, MouseEventHandler, PropsWithChildren, ReactElement, Ref } from 'react'
import { memo, useRef } from 'react'
import styled from 'styled-components'
import IconButton from '../Button/IconButton'
import TextButton from '../Button/TextButton'
import LineTimesIcon from '../Icons/line/LineTimesIcon'
import BodyTextBlock from '../TextBlocks/BodyTextBlock'
import { useAppHeaderObserver } from 'hooks/useAppLayoutObservers'
import { bannerKindStyles } from './styles'
import type { BannerKind } from './types'

const BannerSheet = styled.div`
  ${bannerKindStyles}
`

const BannerContainer = styled(LayoutContainer)`
  display: grid;

  &.density-comfortable { padding: ${rem(16)}; }
  &.density-compact { padding: ${rem(12)}; }
  &.density-super-compact { padding: ${rem(8)} ${rem(12)}; }

  &.align-start {
    grid-template:
      "content . dismiss" auto
      "actions actions actions" auto / auto 1fr min-content;
  }
  &.align-center {
    grid-template:
      ". content . dismiss" auto
      "actions actions actions actions" auto / 1fr auto 1fr min-content;
  }

  ${mediaQueryUp.tablet} {
    align-items: center;
    &.density-comfortable { padding: ${rem(16)} ${rem(20)}; }
    &.density-compact { padding: ${rem(12)} ${rem(20)}; }
    &.density-super-compact { padding: ${rem(8)} ${rem(20)}; }

    &.align-on-tablet-start {
      grid-template: "content actions . dismiss" min-content / auto auto 1fr min-content;
    }
    &.align-on-tablet-center {
      grid-template: ". content actions . dismiss" min-content / 1fr auto auto 1fr min-content;
    }
  }
`

const BannerContent = styled(BodyTextBlock)`
  grid-area: content;
`

const BannerActionsGroup = styled(Group)`
  grid-area: actions;
  margin: ${rem(8)} 0 0 0;

  ${mediaQueryUp.tablet} {
    margin: 0 0 0 ${rem(12)};
  }
`

const BannerDismissButton = styled(IconButton)`
  grid-area: dismiss;
  margin: 0 ${rem(-4)} 0 ${rem(8)};
  align-self: center;

  ${mediaQueryUp.tablet} {
    margin: 0 0 0 ${rem(24)};
  }
`

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  ref?: Ref<HTMLDivElement>
  /** sets the colour of the banner */
  kind: BannerKind
  /**
   * sets the horizontal alignment of the content
   * @default 'start'
   * */
  align?: 'start' | 'center'
  /**
   * @default comfortable
   */
  density?: 'comfortable' | 'compact' | 'super-compact'
  /**
   * sets the horizontal alignment of the content on tablet breakpoint and higher
   * @default falls back to `align`
   * */
  tabletAlign?: 'start' | 'center'
  /** sets the icon at the start of the description */
  icon?: ReactElement
  /** sets the textual content that sits above the description as a heading */
  heading?: string | ReactElement
  className?: string
  id?: string
  /**
   * sets the props passed into the primary action
   *  */
  primaryActionButtonProps?: Omit<ComponentProps<typeof TextButton>, 'kind' | 'variant'>
  /**
   * sets the kind for the primary action button
   * @default secondary
   */
  primaryActionButtonKind?: 'primary' | 'secondary' | 'tertiary'
  /**
   * sets the props passed into the secondary action
   *  */
  secondaryActionButtonProps?: Omit<ComponentProps<typeof TextButton>, 'kind' | 'variant'>
  /** invoked when the dismiss button is clicked */
  onDismiss?: MouseEventHandler<HTMLButtonElement>
  /** sets the position type for header observation */
  position?: 'static' | 'sticky'
}

function PageBanner({
  children,
  kind,
  align = 'start',
  density = 'comfortable',
  tabletAlign,
  icon,
  heading,
  className,
  id,
  primaryActionButtonProps,
  primaryActionButtonKind = 'secondary',
  secondaryActionButtonProps,
  onDismiss,
  position = 'static',
  ...rest
}: Props) {
  const bannerRef = useRef<HTMLDivElement>(null)
  useAppHeaderObserver(bannerRef, position)

  return <BannerSheet
    {...rest}
    ref={bannerRef}
    id={id}
    className={clsx(
      className,
      `kind-${kind}`,
    )}
  >
    <BannerContainer
      className={clsx(
      `align-${align}`,
        `align-on-tablet-${tabletAlign ?? align}`,
        `density-${density}`,
      )}
    >
      <BannerContent
        startIcon={icon}
        variant="medium"
        align={align}
        tabletAlign={tabletAlign}
      >
        <Group direction="vertical" gap={4}>
          {!!heading && <b>{heading}</b>}
          <div>
            {children}
          </div>
        </Group>
      </BannerContent>

      {(primaryActionButtonProps || secondaryActionButtonProps) && <BannerActionsGroup
        direction="horizontal"
        horizontalAlign="end"
        gap={8}
      >
        {secondaryActionButtonProps && <TextButton {...secondaryActionButtonProps} kind="tertiary" />}
        {primaryActionButtonProps && <TextButton {...primaryActionButtonProps} kind={primaryActionButtonKind} />}
      </BannerActionsGroup>}

      {onDismiss && <BannerDismissButton
        kind="tertiary"
        size="small"
        aria-controls={id}
        onClick={onDismiss}
        variant="default"
      >
        <LineTimesIcon />
      </BannerDismissButton>}
    </BannerContainer>
  </BannerSheet>
}

export default memo(PageBanner, shallowEqualWithNestedProps(['primaryActionButtonProps', 'secondaryActionButtonProps']))
